import { environment } from 'nsdi-volto-frontend/config/environments';

/**
 * Card Display Field Schema
 * Defines a single field to display on cards
 */
const cardDisplayFieldSchema = {
  title: 'Card Field',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['field', 'label'],
    },
  ],
  properties: {
    field: {
      title: 'Field Name',
      description:
        'API field name (e.g., format, fd__acquired, fs__provider, format_category)',
      type: 'string',
    },
    label: {
      title: 'Display Label',
      description: 'Label shown on the card (e.g., Format, Acquired)',
      type: 'string',
    },
  },
  required: ['field', 'label'],
};

const sidebarFacetFieldSchema = {
  title: 'Sidebar Facet Field',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: [
        'field',
        'label',
        'categoryPath',
        'useShortLabels',
        'maxValues',
        'valueCategories',
      ],
    },
  ],
  properties: {
    field: {
      title: 'Facet Field',
      description:
        'Exact facet.field value sent to Voyager, including Solr local params when needed (for example {!ex=grp_NSDI}grp_NSDI or tree_nsdi).',
      type: 'string',
    },
    label: {
      title: 'Sidebar Label',
      description: 'Label shown for this sidebar section.',
      type: 'string',
    },
    categoryPath: {
      title: 'Category Path',
      description:
        'Optional hierarchical grouping for this facet field. Use > between levels, for example Imagery > Format.',
      type: 'string',
    },
    useShortLabels: {
      title: 'Use Short Value Labels',
      description:
        'Shortens displayed facet values when possible, for example application/csv to csv.',
      type: 'boolean',
      default: false,
    },
    maxValues: {
      title: 'Max Facet Values',
      description:
        'Maximum number of values to request and show for this facet. Use -1 to request all values.',
      type: 'number',
      minimum: -1,
    },
    valueCategories: {
      title: 'Value Categories JSON',
      description:
        'Optional JSON array used to group facet values under subcategories. Supports nested hierarchy with "children" for subgroups. Use "contains" for partial matching.',
      type: 'string',
      widget: 'expandable_textarea',
      default: JSON.stringify(
        [
          {
            label: 'Geospatial',
            children: [
              {
                label: 'Data Formats',
                contains: [
                  'geo+json',
                  'geo-json',
                  'geojson',
                  'geopackage',
                  'sqlite3',
                  'kml',
                  'kmz',
                ],
              },
              {
                label: 'GIS Services and Layers',
                children: [
                  {
                    label: 'Esri',
                    contains: ['esri', 'arcgis'],
                  },
                  {
                    label: 'OGC Services',
                    children: [
                      {
                        label: 'CSW',
                        contains: ['csw_record', 'csw_xml'],
                      },
                      {
                        label: 'WMS',
                        contains: ['wms_layer', 'wms_xml'],
                      },
                      {
                        label: 'WMTS',
                        contains: ['wmts_layer', 'wmts_xml'],
                      },
                    ],
                  },
                  {
                    label: 'GeoTools',
                    contains: ['geotools'],
                  },
                ],
              },
            ],
          },
          {
            label: 'Tabular and Spreadsheet',
            children: [
              {
                label: 'Spreadsheets',
                children: [
                  {
                    label: 'Microsoft Excel',
                    contains: [
                      'excel',
                      'ms-excel',
                      'xls',
                      'xlsx',
                      'spreadsheetml',
                    ],
                  },
                  {
                    label: 'Other Spreadsheets',
                    contains: ['lotus-1-2-3'],
                  },
                ],
              },
              {
                label: 'Delimited Files',
                contains: ['csv', 'tsv', 'tab'],
              },
              {
                label: 'Database Formats',
                contains: ['dbf', 'dbase', 'msaccess', 'ms-access', 'access'],
              },
            ],
          },
          {
            label: 'Documents and Text',
            children: [
              {
                label: 'Standard Documents',
                contains: ['pdf', 'plain', 'txt', 'text'],
              },
              {
                label: 'Web Documents',
                children: [
                  {
                    label: 'HTML',
                    contains: ['html', 'htm', 'sgml'],
                  },
                  {
                    label: 'Server-Side Scripts',
                    contains: ['aspdotnet', 'php', 'cgi', 'coldfusion'],
                  },
                ],
              },
              {
                label: 'Office Documents',
                contains: ['google-apps.document', 'wordprocessingml'],
              },
              {
                label: 'Other Text',
                contains: ['ascii-art'],
              },
            ],
          },
          {
            label: 'Structured Data and Markup',
            children: [
              {
                label: 'JSON',
                values: ['json', 'application/json', 'text/json', 'api+json'],
                contains: ['json'],
              },
              {
                label: 'XML',
                values: ['xml', 'application/xml', 'text/xml'],
                contains: ['rdf+xml'],
              },
              {
                label: 'HTTP',
                values: ['http', 'application/http'],
              },
            ],
          },
          {
            label: 'Archives and Packages',
            children: [
              {
                label: 'Compressed Archives',
                contains: ['zip', 'rar', 'tar'],
              },
              {
                label: 'Binary and Other',
                contains: ['octet-stream', 'multipart', 'pgp-signature'],
              },
            ],
          },
          {
            label: 'Scientific and Analytical',
            children: [
              {
                label: 'Mathematical',
                children: [
                  {
                    label: 'Wolfram/Mathematica',
                    contains: ['mathematica', 'wolfram'],
                  },
                ],
              },
              {
                label: 'Statistical',
                contains: ['stata'],
              },
              {
                label: 'Chemical',
                contains: ['chemical', 'mdl-sdfile', 'xyz'],
              },
              {
                label: 'Visualization',
                contains: ['ecowin', 'chart'],
              },
            ],
          },
          {
            label: 'Catalogs and Metadata',
            children: [
              {
                label: 'CKAN',
                contains: ['ckan'],
              },
              {
                label: 'DCAT',
                contains: ['dcat'],
              },
              {
                label: 'Classification',
                contains: ['nsdi-classification', 'classification'],
              },
            ],
          },
          {
            label: 'Media',
            children: [
              {
                label: 'Image',
                contains: ['jpeg', 'jpg', 'png', 'gif', 'image'],
              },
              {
                label: 'Audio',
                contains: ['audio', 'sid'],
              },
              {
                label: 'Video',
                contains: ['video', 'mp4', 'quicktime'],
              },
            ],
          },
          {
            label: 'Other and Unknown',
            contains: ['unknown', 'ms-pki', 'lotus-notes'],
          },
        ],
        null,
        2,
      ),
    },
  },
  required: ['field'],
};

const sidebarFacetParamSchema = {
  title: 'Sidebar Facet Param',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['key', 'value'],
    },
  ],
  properties: {
    key: {
      title: 'Parameter Key',
      description: 'Additional query parameter applied to the facet request.',
      type: 'string',
    },
    value: {
      title: 'Parameter Value',
      description: 'Value for the additional query parameter.',
      type: 'string',
    },
  },
  required: ['key', 'value'],
};

const searchScopeButtonSchema = {
  title: 'Search Scope Button',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['label', 'fq'],
    },
  ],
  properties: {
    label: {
      title: 'Button Label',
      description: 'Text shown on the button (e.g., Metadata).',
      type: 'string',
    },
    fq: {
      title: 'Filter Query (fq)',
      description:
        'Raw Solr filter query applied when this button is active, e.g. format:("dcat dataset" OR "iso 19115" OR "application/rss+xml").',
      type: 'string',
      widget: 'textarea',
    },
  },
  required: ['label', 'fq'],
};

// Per-page branding images. Same object_browser shape the Data Manager Admin
// block uses, but stored on the block so each search page can have its own.
// Leaving one unset falls back to the site-wide image (see utils/blockImage.ts).
const imageBrowserField = {
  widget: 'object_browser' as const,
  mode: 'image',
  type: 'object',
  selectedItemAttrs: ['Title', '@id', '@type', 'image_field', 'image_scales'],
  allowExternals: false,
};

const MetaDataSearchPageBlockSchema = {
  title: 'Voyager Search Page',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: [
        'placeholder',
        'detailsPagePath',
        'showMap',
        'hierarchyTreeBase',
      ],
    },
    {
      id: 'api',
      title: 'API Configuration',
      fields: ['voyagerBaseUrl', 'voyagerConfigId'],
    },
    {
      id: 'agol',
      title: 'AGOL',
      fields: ['arcgisUrl', 'agolEligibleFormats'],
    },
    {
      id: 'branding',
      title: 'Branding',
      fields: [
        'thumbnailImage',
        'headerBanner',
        'footerBanner',
        'accentColor',
      ],
    },
    {
      id: 'searchScope',
      title: 'Search Scope',
      fields: ['searchScopeLabel', 'searchScopeButtons'],
    },
    {
      id: 'cardDisplay',
      title: 'Card Display',
      fields: ['cardColumns', 'cardDisplayFields', 'cardBadgeField'],
    },
    {
      id: 'sidebar',
      title: 'Sidebar',
      fields: [
        'sidebarFacetFields',
        'sidebarFacetParams',
        'sidebarFacetValueCategoriesCollapsedByDefault',
        'sidebarFacetLimit',
        'sidebarFacetMinCount',
        'sidebarApiTestLink',
      ],
    },
  ],
  properties: {
    thumbnailImage: {
      title: 'Page Logo',
      description:
        'Logo shown beside the search bar on this page. Leave empty to use the site-wide thumbnail.',
      ...imageBrowserField,
    },
    headerBanner: {
      title: 'Header Banner',
      description:
        'Full-width banner behind the header on this page. Leave empty to use the site-wide banner.',
      ...imageBrowserField,
    },
    footerBanner: {
      title: 'Footer Banner',
      description:
        'Full-width banner at the bottom of this page. Leave empty to use the site-wide banner.',
      ...imageBrowserField,
    },
    accentColor: {
      // Plain string, not widget: 'color_picker' - Volto's ColorPickerWidget is a
      // fixed-palette chooser and requires a `colors` prop it has no default for,
      // so declaring it without one throws on render. An accent colour here is a
      // free-form hex per agency, which a palette cannot express.
      title: 'Accent Color',
      description:
        'Hex colour for this page, e.g. #217821. Defaults to green if unset.',
      type: 'string',
    },
    arcgisUrl: {
      title: 'ArcGIS Online URL',
      description:
        'Full URL to the ArcGIS Online instance, consumed by the Open In AGOL component.',
      type: 'string',
    },
    placeholder: {
      title: 'Search Placeholder',
      description: 'Placeholder text for the search input',
      type: 'string',
    },
    detailsPagePath: {
      title: 'Details Page Path',
      description:
        'The path to navigate to when clicking a result card (e.g., /voyager-details). The item id will be appended as ?id=<itemId>',
      type: 'string',
      default: '/voyager-details',
    },
    showMap: {
      title: 'Show Map',
      description: 'Display the Area of Interest map in the sidebar',
      type: 'boolean',
      default: true,
    },
    hierarchyTreeBase: {
      title: 'Hierarchy Tree Base',
      description:
        'Full hierarchy path; the last segment is treated as root and only its children are displayed (e.g. GlobalGEO/AmeriGEO/NSDI/GeoPlatform).',
      type: 'string',
      widget: 'hierarchy_tree_base',
    },
    voyagerBaseUrl: {
      title: 'Voyager API Base URL',
      description:
        'The base URL for the Voyager Search API. Options: https://odn.voyagersearch.com, https://ogc-sdi.voyagersearch.com, https://voyager.fas.usda.gov/voyager',
      type: 'string',
      default: environment.voyager.baseUrl,
    },
    voyagerConfigId: {
      title: 'Voyager Config ID',
      description:
        'The display configuration ID. Use D18D9C907361 for ODN, default for OGC-SDI, D176678659AD for USDA FAS',
      type: 'string',
      default: environment.voyager.configId,
    },
    searchScopeLabel: {
      title: 'Search Scope Label',
      description:
        'Text shown before the search scope buttons (e.g., "Search in:"). Leave empty to hide the label.',
      type: 'string',
      default: 'Search in:',
    },
    searchScopeButtons: {
      title: 'Search Scope Buttons',
      description:
        'Buttons that let users narrow results to a subset. Each button applies its raw Solr fq when active; clicking the active button clears it. Leave empty to hide the control.',
      type: 'array',
      items: {
        type: 'object',
        schema: searchScopeButtonSchema,
      },
      default: [
        {
          '@id': 'scope-metadata',
          label: 'Metadata',
          fq: 'format:("dcat dataset" OR "iso 19115" OR "application/rss+xml")',
        },
      ],
      widget: 'object_list',
      schema: searchScopeButtonSchema,
    },
    cardColumns: {
      title: 'Cards Per Row',
      description: 'Number of cards to display horizontally (1-6)',
      type: 'number',
      minimum: 1,
      maximum: 6,
      default: 4,
    },
    cardDisplayFields: {
      title: 'Card Display Fields',
      description:
        'Fields to display on cards. Common fields: format, fd__acquired, fs__provider, format_category, format_type, bbox',
      type: 'array',
      items: {
        type: 'object',
        schema: cardDisplayFieldSchema,
      },
      default: [
        { '@id': 'field-1', field: 'format', label: 'Format' },
        { '@id': 'field-2', field: 'fd__acquired', label: 'Acquired' },
        { '@id': 'field-3', field: 'fs__provider', label: 'Provider' },
      ],
      widget: 'object_list',
      schema: cardDisplayFieldSchema,
    },
    cardBadgeField: {
      title: 'Card Badge Field',
      description:
        'Field name to display as badge overlay on card thumbnail (e.g., status_text, event, fs__groundcontrol). Leave empty to hide badge.',
      type: 'string',
      default: '',
    },
    agolEligibleFormats: {
      title: 'AGOL Eligible Formats',
      description:
        'Comma-separated dataset format values that show the "Open in AGOL" button (e.g., arcgis-map-server-layer, arcgis-feature-layer). Leave empty to use the default.',
      type: 'string',
      default: 'arcgis-map-server-layer',
    },
    sidebarFacetFields: {
      title: 'Sidebar Facet Fields',
      description:
        'Ordered list of facet.field values that should be requested and rendered in the sidebar.',
      type: 'array',
      items: {
        type: 'object',
        schema: sidebarFacetFieldSchema,
      },
      widget: 'object_list',
      schema: sidebarFacetFieldSchema,
    },
    sidebarFacetParams: {
      title: 'Sidebar Facet Params',
      description:
        'Extra query parameters needed by special facets, such as tree facet sorting.',
      type: 'array',
      items: {
        type: 'object',
        schema: sidebarFacetParamSchema,
      },
      widget: 'object_list',
      schema: sidebarFacetParamSchema,
    },
    sidebarFacetValueCategoriesCollapsedByDefault: {
      title: 'Collapse Value Categories By Default',
      description:
        'When enabled, grouped facet value categories start collapsed. When disabled, they start expanded.',
      type: 'boolean',
      default: true,
    },
    sidebarFacetLimit: {
      title: 'Sidebar Facet Limit',
      description:
        'Maximum number of facet values requested per sidebar section.',
      type: 'number',
      default: 11,
      minimum: 1,
    },
    sidebarFacetMinCount: {
      title: 'Sidebar Facet Min Count',
      description: 'Minimum count required for a facet value to be returned.',
      type: 'number',
      default: 1,
      minimum: 0,
    },
    sidebarApiTestLink: {
      title: 'Sidebar API Test Link',
      description:
        'Click to test the sidebar facets API call with current configuration.',
      widget: 'sidebar_api_test_link',
    },
  },
  required: [],
};

export default MetaDataSearchPageBlockSchema;
